import InfoIcon from "@/assets/icons/base64/InfoIcon"
import InfoIconGreen from "@/assets/icons/base64/InfoIconGreen"
import GiftIcon from "@/assets/icons/gift.svg"
import OpenIcon from "@/assets/icons/open.svg"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { currentSlotAtom, rateLimiterAtom } from "@/atoms/pools"
import {
  DEFAULT_STE_SYMBOL,
  INITIAL_REL_PRICE,
  NULL_PRICE,
  PriceKey,
  renecPriceDataAtom,
  solanaTokenOraclePricesAtom,
} from "@/atoms/priceState"
import { networkAtom } from "@/atoms/settings"
import { selectedPoolAtom } from "@/atoms/shared"
import { selectedSolendObligationAtom } from "@/atoms/solendObligations"
import { walletAssetsAtom } from "@/atoms/wallet"
import Button from "@/components/base/Button"
import { ModalTxConfirm } from "@/components/modalTxConfirm"
import { ModalTxResult } from "@/components/modalTxResult"
import { RelendModal } from "@/components/relendModal"
import { RelendTable } from "@/components/relendTable"
import EmptyRelendTable from "@/components/relendTable/emptyRelendTable"
import { RelendTooltip } from "@/components/relendTooltip"
import { Stat } from "@/components/stat"
import useActionOnChain from "@/hooks/useActionOnchain"
import useMobileDevice from "@/hooks/useMobileDevice"
import useTrans from "@/hooks/useTrans"
import { useTransactionHandler } from "@/hooks/useTransactionHandler"
import useWalletConnect from "@/hooks/useWalletConnect"
import { withdrawConfigs } from "@/utils/actions/withdraw"
import {
  DEFAULT_DECIMALS,
  DEPOSIT,
  LOCALE_EN,
  LOCALE_VI,
  RE_NGN,
  RE_USD,
  SKELETON_VALUE,
  USDS_APY,
  WITHDRAW,
  ZERO_DECIMALS,
} from "@/utils/constants"
import { HelperText, ObligationReserveType, TransactionDetail } from "@/utils/dataTypes"
import { TokenSymbol, tokenDecimalDigits } from "@/utils/helpers"
import { LOGO_SIZES, getTokenLogoDisplay } from "@/utils/logo"
import { RplToken } from "@/utils/modals/RplToken"
import { formatPercent, formatToken, formatUsd } from "@/utils/numberFormatter"
import { getSTERewardInfo } from "@/utils/supply-to-earn.utils"
import {
  WHITELIST_SYMBOL_MY_SUPPLY_RESERVES,
  WHITELIST_SYMBOL_SUPPLY_ACTIONS,
  WHITELIST_SYMBOL_WITHDRAW_ACTIONS,
  isSymbolWhitelisted,
} from "@/utils/whitelists"
import { Box, Chip, Divider, Link, Stack, TableCell, TableRow, Typography } from "@mui/material"
import {
  ActionEnum,
  REL,
  RE_VND,
  SupplyToEarnApy,
  SupplyToEarnReward,
  USDS,
  calculateNewRatesAfterRefreshReserve,
} from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"
import Decimal from "decimal.js"
import { useAtom, useAtomValue } from "jotai"
import Image from "next/image"
import NextLink from "next/link"
import { useRouter } from "next/router"
import React, { useCallback, useMemo, useState } from "react"
import { useQuery } from "react-query"
import {
  DEFAULT_VIEW_MORE_STE_LINK,
  ObligationProxyTransactionHistory,
  VIEW_MORE_STE_LINKS,
  calculateEstimatedEarnedByObligationHistory,
  formatAPY,
  getLinkExplorerAddress,
  getSupplyBorrowSortingList,
} from "../../commons"
import { SupplyPriceChangeDisplay, SupplyPriceChangeDisplayProps } from "../body/components/SupplyPriceChangeDisplay"
import * as styles from "../styles"
import { ReserveClaimTokenType } from "../types"

/* dayjs.extend(utc)
dayjs.extend(duration)

const getCountdownToNextUTC = () => {
  const now = dayjs().utc()

  // Get the next day at 00:00 UTC
  const nextUTC = dayjs.utc().startOf("day").add(1, "day")

  const diff = nextUTC.diff(now)

  const countdown = dayjs.duration(diff).format("HH:mm:ss")

  return countdown
} */

const getFormattedEarnedAmount = (
  isUSDS: boolean,
  earnedAmountToDisplay: number,
  reserve?: any,
  isFiatToken?: boolean,
) => {
  if (isUSDS && earnedAmountToDisplay === 0) {
    return SKELETON_VALUE
  }

  return formatToken(
    earnedAmountToDisplay,
    tokenDecimalDigits[reserve?.symbol as TokenSymbol],
    false,
    false,
    isFiatToken ? BigNumber.ROUND_UP : undefined,
    false,
    {
      ...styles.textLeft,
      ...styles.textBold,
    },
  )
}

const SupplyView = ({
  doesHaveSupply,
  supplyToEarnApys,
  supplyToEarnRewards,
  handleOpenSupplyWithdrawPopup,
}: {
  doesHaveSupply: boolean
  supplyToEarnApys: SupplyToEarnApy[]
  supplyToEarnRewards: SupplyToEarnReward[]
  handleOpenSupplyWithdrawPopup: (item: ObligationReserveType, type: typeof DEPOSIT | typeof WITHDRAW) => void
}) => {
  const trans = useTrans()
  const walletAssets = useAtomValue(walletAssetsAtom)
  const currentSlot = useAtomValue(currentSlotAtom)
  const [selectedPool] = useAtom(selectedPoolAtom)
  const [selectedMainObligation] = useAtom(selectedMainObligationAtom)
  const [selectedSolendObligation] = useAtom(selectedSolendObligationAtom)
  const [rateLimiter] = useAtom(rateLimiterAtom)
  const renecPriceData = useAtomValue(renecPriceDataAtom)
  const network = useAtomValue(networkAtom)

  const { address, isRenecChain } = useWalletConnect()
  const { actionHandler } = useActionOnChain()
  const { handleClaimToEarnResult } = useTransactionHandler()
  const solanaTokenOraclePrices = useAtomValue(solanaTokenOraclePricesAtom)
  const { data: obligationProxyHistory } = useQuery(
    ["fetchObligationProxyHistory", address, selectedSolendObligation?.address],
    (): Promise<ObligationProxyTransactionHistory[]> | undefined => {
      // TODO: remove this after testing is done
      // if (address && selectedSolendObligation?.address && !isRenecChain) {
      //   return fetchObligationProxyHistory(address, selectedSolendObligation?.address)
      // }
      return undefined
    },
    {
      staleTime: 10000,
    },
  )

  /*   const { refreshSteRewards } = useSupplyToEarn()
  const [isRefreshingSTERewards, setIsRefreshingSTERewards] = useState(false)
  const [timeleftTillNextDay, setTimeleftTillNextDay] = useState<string>(getCountdownToNextUTC())
 */
  const isMobileDevice = useMobileDevice()
  const router = useRouter()
  const locale = router.locale === LOCALE_VI ? LOCALE_VI : LOCALE_EN

  const [openConfirmClaimToEarnModal, setOpenConfirmClaimToEarnModal] = useState(false)
  const [reserveClaimToken, setReserveClaimToken] = useState<ReserveClaimTokenType>()
  const [transactionDetail, setTransactionDetail] = useState<TransactionDetail>({
    isError: true,
    isActive: false,
    ctaButtonLink: "",
    title: "",
    content: "",
  })
  const [loading, setLoading] = useState<boolean>(false)
  const [helperText, setHelperText] = useState<HelperText>({
    content: "",
    color: "",
  })

  const defaultSupplyTokenUSDT = useMemo(() => {
    const reserve = selectedPool?.reserves?.find((r) => r.symbol === RE_USD)

    return { reserveAddress: reserve?.address ?? "" } as ObligationReserveType
  }, [selectedPool])

  const handleClaimToEarn = useCallback(async () => {
    if (!selectedMainObligation || !reserveClaimToken) return

    setLoading(true)
    const submitAmountNum = reserveClaimToken.totalRewardAmount ?? 0
    const roundedAmount = new BigNumber(submitAmountNum).shiftedBy(reserveClaimToken.reserveDecimals).toFixed(0)

    const result = await actionHandler({
      network,
      action: ActionEnum.CLAIM_TO_EARN,
      amount: roundedAmount,
      symbol: reserveClaimToken.reserveSymbol,
      supplyToEarnApys,
      supplyToEarnRewards,
    })

    handleClaimToEarnResult({
      result,
      trans,
      amount: reserveClaimToken.totalRewardAmount,
      reserveSymbol: reserveClaimToken.reserveSymbol,
      rewardSymbol: reserveClaimToken.rewardSymbol,
      walletAddress: address,
      obligationAddress: selectedMainObligation?.address,
      setTransactionDetail,
      setHelperText,
    })

    setLoading(false)
  }, [
    selectedMainObligation,
    reserveClaimToken,
    actionHandler,
    network,
    trans,
    supplyToEarnApys,
    supplyToEarnRewards,
    handleClaimToEarnResult,
    address,
  ])

  /*   const handleRefreshSTERewards = useCallback(async () => {
    setIsRefreshingSTERewards(true)
    const result = await refreshSteRewards()
    handleRefreshSTERewardResult({ result })
    setIsRefreshingSTERewards(false)
  }, [handleRefreshSTERewardResult, refreshSteRewards])
 */
  const handleOpenModal = useCallback(
    (rewardSymbol: string, reserveSymbol: string, totalRewardNumber: number, reserveDecimals?: number) => {
      setReserveClaimToken({
        rewardSymbol,
        reserveSymbol,
        reserveDecimals: reserveDecimals ?? DEFAULT_DECIMALS,
        totalRewardAmount: totalRewardNumber,
      })
      setOpenConfirmClaimToEarnModal(true)
    },
    [],
  )

  const handleCloseModal = useCallback(() => {
    if (!loading) {
      setTransactionDetail({
        isError: true,
        isActive: false,
        ctaButtonLink: "",
        title: "",
        content: "",
      })
      setHelperText({ content: "", color: "" })
      setOpenConfirmClaimToEarnModal(false)
      setReserveClaimToken(undefined)
    }
  }, [loading])

  const mySupplyHeaders = useMemo(
    () => (
      <>
        <TableCell>{trans.dashboard.table.asset}</TableCell>
        <TableCell>
          <Stack direction='row' gap={0.5}>
            {trans.dashboard.table.supplied}
            <RelendTooltip title={trans.dashboard.table.supplied_info} arrow>
              <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
            </RelendTooltip>
          </Stack>
        </TableCell>
        <TableCell>{trans.dashboard.table.price_change}</TableCell>
        <TableCell>{trans.dashboard.table.supply_apy}</TableCell>
        <TableCell>{trans.dashboard.table.earned}</TableCell>
        <TableCell>{trans.dashboard.table.actions}</TableCell>
      </>
    ),
    [trans],
  )

  const desiredSymbolSortingOrder = useMemo(() => getSupplyBorrowSortingList(network), [network])

  const mySupplyItems = useMemo(() => {
    if (!doesHaveSupply) return <></>
    const solendDeposits = selectedSolendObligation?.deposits
    const mainProgramDeposits = selectedMainObligation?.deposits

    return [...(solendDeposits ?? []), ...(mainProgramDeposits ?? [])]
      .sort((a, b) => {
        const indexA = a.symbol ? desiredSymbolSortingOrder.indexOf(a.symbol) : -1
        const indexB = b.symbol ? desiredSymbolSortingOrder.indexOf(b.symbol) : -1

        // If the token is not in the desiredSymbolSortingOrder, move it to the end by assigning a high index value
        const maxIndex = desiredSymbolSortingOrder.length
        return (indexA === -1 ? maxIndex : indexA) - (indexB === -1 ? maxIndex : indexB)
      })
      .map((item, index) => {
        const symbol = item.symbol ?? ""
        const isUSDS = symbol === USDS

        if (!isSymbolWhitelisted(symbol, WHITELIST_SYMBOL_MY_SUPPLY_RESERVES)) {
          return <React.Fragment key={`${symbol}-my-supply-${index}`}></React.Fragment>
        }

        const reserve = selectedPool?.reserves.find((r) => r.address === item.reserveAddress)
        const withdrawMax = reserve
          ? withdrawConfigs.calculateMax(reserve, walletAssets, selectedMainObligation, rateLimiter, currentSlot)
          : BigNumber(0)
        let cTokenExchangeRate: BigNumber = reserve?.cTokenExchangeRate ?? BigNumber(0)
        if (reserve && currentSlot) {
          const { newCTokenExchangeRate } = calculateNewRatesAfterRefreshReserve(reserve, currentSlot)
          cTokenExchangeRate = newCTokenExchangeRate
        }
        const suppliedAmount = cTokenExchangeRate.isGreaterThan(0)
          ? new BigNumber(item.depositedAmountDecimals.toString()).multipliedBy(cTokenExchangeRate)
          : item.amount || new BigNumber(0)
        const totalTokenSupplied = suppliedAmount.isLessThan(withdrawMax) ? withdrawMax : suppliedAmount
        const baseSupplyAPY = isUSDS ? new BigNumber(USDS_APY) : reserve?.supplyAPYInterest ?? BigNumber(0)

        // TODO: update when script is done
        // const earnedAmount = totalTokenSupplied
        //   .minus(item.initialUnderlyingAmount)
        //   .decimalPlaces(reserve?.decimals ?? DEFAULT_DECIMALS)
        //   .toNumber()
        const normalEarnedAmount = totalTokenSupplied
          .minus(item.depositedAmountDecimals)
          .decimalPlaces(reserve?.decimals ?? DEFAULT_DECIMALS)
          .toNumber()
        const earnedAmount = isUSDS
          ? calculateEstimatedEarnedByObligationHistory(
              obligationProxyHistory,
              baseSupplyAPY,
              symbol,
              reserve?.decimals ?? DEFAULT_DECIMALS,
            )
          : normalEarnedAmount
        const earnedAmountToDisplay = earnedAmount < 0 ? 0 : earnedAmount
        const isFiatToken = symbol === RE_VND || symbol === RE_NGN

        let marketPrice = formatUsd(reserve?.minPrice ?? 0)
        if (isFiatToken) {
          marketPrice = formatUsd(reserve?.minPrice ?? 0, 6)
        }

        const suppliedAmountDisplay = (
          <Typography className='font-semibold text-neutral-900 text-sm'>
            {formatToken(
              totalTokenSupplied,
              tokenDecimalDigits[reserve?.symbol as TokenSymbol],
              false,
              false,
              isFiatToken ? BigNumber.ROUND_UP : undefined,
              false,
              styles.supplyBorrowCell,
            )}
          </Typography>
        )
        const suppliedAmountUSD = formatUsd(totalTokenSupplied.multipliedBy(reserve?.minPrice ?? 0))
        const steReward = supplyToEarnRewards.find((ste) => ste.reserve === item.reserveAddress)
        const steRewardSymbol = steReward?.symbol ?? DEFAULT_STE_SYMBOL
        const steRewardOraclePrice =
          solanaTokenOraclePrices && steReward ? solanaTokenOraclePrices[steReward.address]?.spotPrice : 0
        const steRewardTwapFeedPrice = renecPriceData[steRewardSymbol as PriceKey] ?? item.price.toNumber()
        const lastResortRewardPrice = steRewardSymbol === REL ? INITIAL_REL_PRICE : NULL_PRICE
        const steRewardPrice =
          steRewardOraclePrice && steRewardOraclePrice > 0
            ? steRewardOraclePrice
            : isRenecChain
              ? steRewardTwapFeedPrice
              : lastResortRewardPrice
        const iconName = reserve ? RplToken.getTokenDisplayName(reserve.symbol, reserve.name) : null

        const { shouldDisplaySteAPY, shouldDisplayReward, steAPYOnToken } = getSTERewardInfo(
          item,
          steRewardPrice,
          steReward,
        )

        const earnedDisplay = isUSDS ? (
          <Box></Box>
        ) : (
          <Stack alignItems={{ xs: "end", sm: "start" }}>
            <Typography className='font-semibold text-neutral-1000 text-sm flex items-center gap-1'>
              {getFormattedEarnedAmount(isUSDS, earnedAmountToDisplay, reserve, isFiatToken)} {iconName}
            </Typography>
            {shouldDisplayReward && steReward && (
              <Box className='flex gap-1 items-center'>
                <Typography
                  onClick={() =>
                    handleOpenModal(
                      steReward.symbol,
                      symbol,
                      new Decimal(Number(steReward.accumulatedRewardAmount))
                        .toDecimalPlaces(steReward.rewardDecimals, Decimal.ROUND_FLOOR)
                        .toNumber(),
                      reserve?.decimals,
                    )
                  }
                  className='text-orange-600 text-xs rounded w-fit cursor-pointer flex items-center gap-1 px-1 py-[2px] border border-dashed border-orange-600'
                >
                  <Image src={GiftIcon} alt='info-icon' width={12} height={12} />
                  {formatToken(
                    steReward.accumulatedRewardAmount,
                    undefined,
                    false,
                    false,
                    steReward.rewardDecimals === 0 ? BigNumber.ROUND_UP : undefined,
                    false,
                    styles.earnedReward,
                  )}{" "}
                  {steReward.symbol}
                </Typography>
                {/* TODO: update refresh reward button later */}
                {/* <RelendTooltip title={trans.dashboard.table.refresh_reward_tooltip_desc(timeleftTillNextDay)} arrow>
                  <Image
                    className={cn(
                      "cursor-pointer",
                      isRefreshingSTERewards ? "animate-spin cursor-not-allowed opacity-50" : "",
                    )}
                    src={RefreshIconGreen}
                    alt='refresh-icon'
                    width={20}
                    height={20}
                    onClick={handleRefreshSTERewards}
                  />
                </RelendTooltip> */}
              </Box>
            )}
          </Stack>
        )

        const tokenSpecificSTELinks = VIEW_MORE_STE_LINKS[symbol as TokenSymbol]
        const viewMoreSTELink = tokenSpecificSTELinks
          ? tokenSpecificSTELinks[locale]
          : DEFAULT_VIEW_MORE_STE_LINK[locale]

        const supplyAPYDisplay =
          shouldDisplaySteAPY && steReward && isRenecChain ? (
            <Stack gap={0} direction='column' alignItems='start' justifyContent='start'>
              <Box sx={{ ...styles.supplyAPYWrapper, ...styles.supplyAPYCell }}>
                {formatAPY(baseSupplyAPY.plus(steAPYOnToken))}
                <RelendTooltip
                  title={
                    <Box>
                      <Typography component='span'>
                        {locale === LOCALE_VI ? trans.dashboard.table.supply_apy_interest : null}{" "}
                      </Typography>
                      <Typography sx={styles.greenBold} component='span'>
                        {formatAPY(baseSupplyAPY)}{" "}
                      </Typography>
                      <Typography component='span'>
                        {locale === LOCALE_EN ? trans.dashboard.table.supply_apy_interest : null} +{" "}
                      </Typography>
                      <Typography sx={styles.orangeBold} component='span'>
                        {formatAPY(steAPYOnToken)}{" "}
                      </Typography>
                      <Typography component='span'>{`${steReward.name} ${trans.dashboard.table.rewards}.`}</Typography>
                      <br />
                      <Typography>
                        {trans.dashboard.table.max_reward}{" "}
                        <Typography sx={styles.orangeBold} component='span'>
                          {formatToken(
                            new BigNumber(steReward.totalRewards ?? "0")
                              .shiftedBy(-Number(steReward.rewardDecimals))
                              .toString(),
                            ZERO_DECIMALS,
                            false,
                            false,
                            steReward.rewardDecimals === 0 ? BigNumber.ROUND_UP : undefined,
                            false,
                            styles.orangeBold,
                          )}{" "}
                        </Typography>
                        <Typography component='span'>{steReward.name}</Typography>
                      </Typography>
                      <Typography component='span'>
                        {trans.dashboard.table.supply_reward_information}{" "}
                        <Link component={NextLink} href={viewMoreSTELink} target='_blank' sx={styles.orangeLink}>
                          {trans.dashboard.table.here}
                        </Link>
                      </Typography>
                    </Box>
                  }
                  data-testid={`my-supply-borrow-ste-apy-${symbol}`}
                >
                  <Image src={InfoIconGreen} alt='info-icon' width={16} height={16} />
                </RelendTooltip>
              </Box>
              <Box sx={{ ...styles.supplyAPYWrapper, ...styles.earnedAPY }}>
                ({formatPercent(steAPYOnToken)}{" "}
                <Image
                  src={steReward.logoURI}
                  alt={steReward.name || "logo"}
                  width={16}
                  height={16}
                  style={{ borderRadius: "100%" }}
                />
                )
              </Box>
            </Stack>
          ) : (
            <Typography className='text-green-600 font-bold text-sm'>{formatAPY(baseSupplyAPY)}</Typography>
          )

        const explorerMintURL = getLinkExplorerAddress(network, reserve?.mintAddress ?? "")
        const tokenNameDisplay = (
          <Link
            href={explorerMintURL}
            target='_blank'
            className='flex gap-1 items-center no-underline'
            data-testid={`my-supply-borrow-supply-token-name-${index}`}
          >
            <Typography className='text-neutral-900 text-sm'>{iconName}</Typography>
            <Image className='grayscale' src={OpenIcon} alt='click-icon' width={12} height={12} />
          </Link>
        )
        const supplyPriceChangeDisplayProps: SupplyPriceChangeDisplayProps = {
          symbol,
        }
        const supplyPriceChangeDisplay = <SupplyPriceChangeDisplay {...supplyPriceChangeDisplayProps} />

        const canSupplyAction = WHITELIST_SYMBOL_SUPPLY_ACTIONS.includes(symbol)
        const canWithdrawAction = WHITELIST_SYMBOL_WITHDRAW_ACTIONS.includes(symbol)

        const actionButtonsDisplay = (
          <>
            <Button
              variant='primary'
              onClick={() => {
                handleOpenSupplyWithdrawPopup(item, DEPOSIT)
              }}
              data-testid={`my-supply-borrow-supply-button-${index}`}
              className='flex-1'
              disabled={!canSupplyAction}
              size='md'
            >
              {trans.dashboard.table.supply}
            </Button>
            <Button
              variant='secondary'
              onClick={() => {
                handleOpenSupplyWithdrawPopup(item, WITHDRAW)
              }}
              data-testid={`my-supply-borrow-withdraw-button-${index}`}
              className='flex-1'
              disabled={!canWithdrawAction}
              size='md'
            >
              {trans.dashboard.table.withdraw}
            </Button>
          </>
        )

        if (isMobileDevice) {
          return (
            <Stack
              key={`${symbol}-my-supply-${index}`}
              gap={1}
              className={`${index > 0 ? "border-t border-solid border-secondary pt-4 mt-4" : ""}`}
            >
              <Box sx={styles.iconWrapper}>
                {getTokenLogoDisplay(symbol, reserve, LOGO_SIZES.LG)}
                {tokenNameDisplay}
                <Divider
                  orientation='vertical'
                  flexItem
                  className='text-neutral-500'
                  style={{
                    marginTop: "6px",
                    height: "20px",
                    width: "2px",
                  }}
                />
                <Typography className='text-neutral-500 text-xs'>{marketPrice}</Typography>
                {isUSDS && (
                  <RelendTooltip title={trans.dashboard.table.isolated_desc} placement='top'>
                    <Chip
                      sx={{
                        "& .MuiChip-label": {
                          px: 1,
                          fontWeight: 500,
                          fontSize: 11,
                          pointerEvents: "none",
                        },
                        "&.MuiChip-root": {
                          cursor: "inherit", // Inherit cursor from parent
                        },
                      }}
                      clickable={false}
                      label={trans.dashboard.table.isolated}
                      className='bg-primary text-white w-fit h-fit py-[1px]'
                    />
                  </RelendTooltip>
                )}
              </Box>
              <Box>
                <Stat
                  statsWrapper={styles.statWrapper}
                  titleEl={
                    <Box sx={{ ...styles.suppliedWrapper, ...styles.statTitleGray }}>
                      {trans.dashboard.table.supplied}
                      <RelendTooltip title={trans.dashboard.table.supplied_info} arrow>
                        <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
                      </RelendTooltip>
                    </Box>
                  }
                  content={
                    <Stack alignItems='end' gap={0}>
                      <Box className='font-semibold text-neutral-900 text-sm flex items-center gap-1'>
                        {suppliedAmountDisplay} <span className='text-neutral-500 text-xs font-normal'>{symbol}</span>
                      </Box>
                      <Typography className='text-neutral-500 text-xs font-normal'>~{suppliedAmountUSD}</Typography>
                    </Stack>
                  }
                  contentStyle={styles.supplyBorrowCell}
                  statsTitleWrapper={styles.statTitleMobileWrapper}
                />
                <Stat
                  statsWrapper={styles.statWrapper}
                  titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.price_change}</Typography>}
                  content={supplyPriceChangeDisplay}
                  statsTitleWrapper={styles.statTitleMobileWrapper}
                />
                <Stat
                  statsWrapper={styles.statWrapper}
                  titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.supply_apy}</Typography>}
                  content={supplyAPYDisplay}
                  statsTitleWrapper={styles.statTitleMobileWrapper}
                />
                {/* !TODO: remove this after testing is done */}
                {!isUSDS ? (
                  <Stat
                    statsWrapper={styles.statWrapper}
                    titleEl={<Typography sx={styles.statTitleGray}>{trans.dashboard.table.earned}</Typography>}
                    content={earnedDisplay}
                    contentStyle={styles.earnCell}
                    statsTitleWrapper={styles.statTitleMobileWrapper}
                  />
                ) : null}
              </Box>
              <Stack direction='row' alignItems={"center"} gap={1}>
                {actionButtonsDisplay}
              </Stack>
            </Stack>
          )
        }

        return (
          <TableRow sx={styles.tableBodyRow} key={`${symbol}-my-supply-${index}`}>
            <TableCell>
              <Stack direction='row' gap={1} alignItems='start'>
                {getTokenLogoDisplay(symbol, reserve, LOGO_SIZES.MD)}
                <Stack gap={0}>
                  {tokenNameDisplay}
                  <Typography className='text-neutral-500 text-xs'>{marketPrice}</Typography>
                </Stack>
                {isUSDS && (
                  <RelendTooltip title={trans.dashboard.table.isolated_desc} placement='top'>
                    <Chip
                      sx={{
                        "& .MuiChip-label": {
                          px: 1,
                          fontWeight: 500,
                          fontSize: 11,
                        },
                      }}
                      clickable={false}
                      label={trans.dashboard.table.isolated}
                      className='bg-primary text-white w-fit h-fit py-[1px]'
                    />
                  </RelendTooltip>
                )}
              </Stack>
            </TableCell>
            <TableCell>
              <Stack gap={0}>
                <Box className='font-semibold text-neutral-900 text-sm flex items-center gap-1'>
                  {suppliedAmountDisplay} <span className='text-neutral-500 text-xs font-normal'>{symbol}</span>
                </Box>
                <Typography className='text-neutral-500 text-xs font-normal'>~{suppliedAmountUSD}</Typography>
              </Stack>
            </TableCell>
            <TableCell>{supplyPriceChangeDisplay}</TableCell>
            <TableCell>{supplyAPYDisplay}</TableCell>
            <TableCell>{earnedDisplay}</TableCell>
            <TableCell>
              <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1}>
                {actionButtonsDisplay}
              </Stack>
            </TableCell>
          </TableRow>
        )
      })
  }, [
    doesHaveSupply,
    selectedSolendObligation?.deposits,
    selectedMainObligation,
    desiredSymbolSortingOrder,
    selectedPool?.reserves,
    walletAssets,
    rateLimiter,
    currentSlot,
    obligationProxyHistory,
    supplyToEarnRewards,
    solanaTokenOraclePrices,
    renecPriceData,
    isRenecChain,
    locale,
    trans,
    network,
    isMobileDevice,
    handleOpenModal,
    handleOpenSupplyWithdrawPopup,
  ])

  const titleDialog = useMemo(() => {
    if (!transactionDetail.isActive) return

    return transactionDetail.isError ? trans.dashboard.modal.oops : trans.dashboard.modal.congrats
  }, [trans, transactionDetail])

  /* useEffect(() => {
    const interval = setInterval(() => {
      const countdown = getCountdownToNextUTC()
      setTimeleftTillNextDay(countdown)
    }, 1000)

    return () => clearInterval(interval)
  }, []) */

  return (
    <>
      <Stack>
        {doesHaveSupply && !isMobileDevice && (
          <RelendTable
            loading={false}
            headers={mySupplyHeaders}
            items={mySupplyItems}
            loadingRows={10}
            loadingCellsPerRow={5}
          />
        )}

        {doesHaveSupply && isMobileDevice && mySupplyItems}

        {!doesHaveSupply && (
          <EmptyRelendTable
            content={trans.dashboard.table.empty_supply}
            buttonCTA={
              defaultSupplyTokenUSDT && (
                <Button
                  size='md'
                  className='min-w-[130px]'
                  data-testid='my-supply-empty-button'
                  onClick={() => {
                    handleOpenSupplyWithdrawPopup(defaultSupplyTokenUSDT, DEPOSIT)
                  }}
                >
                  {trans.dashboard.table.supply}
                </Button>
              )
            }
          />
        )}
      </Stack>

      {transactionDetail.isActive && (
        <RelendModal
          title={titleDialog}
          isSmallPopup={transactionDetail.isActive}
          hideButtonClose={transactionDetail.isActive}
          open={transactionDetail.isActive}
          handleClose={handleCloseModal}
          data-testid='modal-claim-to-earn'
          useDrawerOnMobile
        >
          {transactionDetail.isActive && (
            <ModalTxResult transactionDetail={transactionDetail} handleCloseModal={handleCloseModal} />
          )}
        </RelendModal>
      )}

      {reserveClaimToken && (
        <RelendModal
          title={trans.dashboard.modal.confirm}
          isSmallPopup
          showCloseButton
          open={openConfirmClaimToEarnModal}
          handleClose={handleCloseModal}
          data-testid='modal-claim-to-earn'
          useDrawerOnMobile
          textButtonClose={trans.dashboard.modal.claim_later}
        >
          <ModalTxConfirm
            title={trans.dashboard.modal.claim_your_reward}
            description={trans.dashboard.modal.claim_your_reward_desc(reserveClaimToken.rewardSymbol)}
            helperText={helperText}
            textConfirm={trans.dashboard.modal.claim_confirm}
            loading={loading}
            handleConfirm={handleClaimToEarn}
          />
        </RelendModal>
      )}
    </>
  )
}

export default SupplyView
